import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setAccountValidating,
  setAccountValid,
  setAccountInvalid,
  clearAccountValidation,
  selectAccountValidation,
} from '../reducers/onboardingSlice';
import { snowflakeApi } from '../services/snowflakeApi';
import Input from 'components/Input';
import { parseAccountFromURL } from 'features/snowflake-accounts/components/SnowflakeCredentialsForm';

/**
 * Props for AccountValidationInput component
 */
interface AccountValidationInputProps {
  /** Current value of the account identifier */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * AccountValidationInput Component
 *
 * Input field with debounced Snowflake account validation showing real-time
 * validation status with loading spinner, success checkmark, or error message.
 *
 * @component
 * @example
 * ```tsx
 * <AccountValidationInput
 *   value={accountIdentifier}
 *   onChange={setAccountIdentifier}
 *   disabled={loading}
 *   required
 * />
 * ```
 */
export const AccountValidationInput: React.FC<AccountValidationInputProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
}) => {
  const dispatch = useAppDispatch();
  const validation = useAppSelector(selectAccountValidation);
  const [debouncedValue, setDebouncedValue] = useState<string>(value);

  /**
   * Validates the account identifier
   */
  const validateAccount = useCallback(
    async (identifier: string) => {
      if (identifier.trim() === '') {
        dispatch(clearAccountValidation());
        return;
      }

      dispatch(setAccountValidating(true));

      try {
        const result = await snowflakeApi.validateSnowflakeAccount(identifier);

        if (result.valid) {
          dispatch(setAccountValid());
        } else {
          dispatch(setAccountInvalid(result.message));
        }
      } catch (error) {
        console.error('Account validation error:', error);
        dispatch(setAccountInvalid('Failed to validate account. Please try again.'));
      }
    },
    [dispatch],
  );

  /**
   * Debounce the validation - wait 500ms after user stops typing
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, 500);

    return () => clearTimeout(timer);
  }, [value]);

  /**
   * Trigger validation when debounced value changes
   */
  useEffect(() => {
    if (debouncedValue !== '' && debouncedValue === value) {
      void validateAccount(debouncedValue);
    }
  }, [debouncedValue, value, validateAccount]);

  /**
   * Handle paste with auto-formatting
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text/plain');
    const parsedAccount = parseAccountFromURL(pastedText);
    onChange(parsedAccount);
  };

  /**
   * Render validation status icon
   */
  const renderValidationIcon = (): React.ReactNode => {
    if (validation.isValidating) {
      return (
        <motion.div
          className="absolute right-3 top-1/4 -translate-y-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          aria-label="Validating account"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="h-5 w-5 border-2 border-dataops-primary-blue border-t-transparent rounded-full"
            aria-hidden="true"
          />
        </motion.div>
      );
    }

    if (validation.isValid) {
      return (
        <motion.div
          className="absolute right-3 top-1/4 -translate-y-1/2"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500 }}
          aria-label="Account is valid"
        >
          <CheckCircleIcon className="h-5 w-5 text-green-highlight" aria-hidden="true" />
        </motion.div>
      );
    }

    if (validation.isInvalid) {
      return (
        <motion.div
          className="absolute right-3 top-1/4 -translate-y-1/2"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500 }}
          aria-label="Account is invalid"
        >
          <XCircleIcon className="h-5 w-5 text-red-highlight" aria-hidden="true" />
        </motion.div>
      );
    }

    return null;
  };

  return (
    <div>
      <label htmlFor="accountIdentifier" className="block text-sm font-medium text-primary-text">
        Account Identifier{' '}
        {required && (
          <span className="text-red-highlight" aria-label="required">
            *
          </span>
        )}
      </label>
      <div className="mt-2 relative">
        <Input
          id="accountIdentifier"
          name="accountIdentifier"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onPaste={handlePaste}
          placeholder="myorg-myaccount"
          required={required}
          disabled={disabled}
          aria-required={required}
          aria-describedby="accountIdentifier-help accountIdentifier-error"
          aria-invalid={validation.isInvalid}
          className={validation.isInvalid ? 'pr-10' : ''}
        />
        {renderValidationIcon()}
      </div>

      {validation.isInvalid && validation.errorMessage !== null && (
        <motion.p
          id="accountIdentifier-error"
          className="mt-2 text-sm text-red-highlight"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          role="alert"
          aria-live="assertive"
        >
          {validation.errorMessage}
        </motion.p>
      )}
    </div>
  );
};
